import { Callout, Button, Divider, Intent } from "@blueprintjs/core";
import React from 'react';
import { useSelector } from 'react-redux';
import SWEngImg from '../../images/sw-eng-image.svg';
import { useVariant } from '../../hooks/useVariant';

const defaultCallouts = [
    {
        title: 'BodaPro',
        body: (
            <>
                This is the first release of BodaPro. It is a lightweight productivity tool.
                <ul>
                    <li>A growing repository of extensions</li>
                    <li>Available on Windows, Mac, and Linux</li>
                </ul>
            </>
        ),
        learnMoreUrl: 'https://bodapro.app',
    },
    {
        title: 'BodaPro Server',
        body: 'Scale your productivity with the server version.',
        learnMoreUrl: 'https://bodapro.app',
        learnMoreLabel: 'Visit site',
    },
    {
        title: 'BodaPro Online',
        body: 'Forget the infrastructure management and focus on your productivity.',
        learnMoreUrl: 'https://bodapro.app',
        learnMoreLabel: 'Visit site',
    },
];

const openLink = (url) => {
    try {
        if (typeof bodapro !== 'undefined' && bodapro?.platformType === 'web') {
            window.open(url, '_blank');
        } else {
            window.bodapro?.openLink(url);
        }
    } catch(e) {
        console.error(e);
    }
};

// A callout action either triggers a BodaPro command (event command, fire-and-forget)
// or opens an external link. `command` takes precedence when both are set.
const runAction = (action) => {
    try {
        if (action?.command) {
            window['bodapro-context']?.triggerCommand(action.command, action.params);
        } else if (action?.url) {
            openLink(action.url);
        }
    } catch (e) {
        console.error('Failed to run callout action:', e);
    }
};

const Welcome = () => {
    const installedFromRedux = useSelector(state => (state.extensions?.installedExtensions || []).includes('bip'));
    const [justInstalledBip, setJustInstalledBip] = React.useState(false);
    const [installing, setInstalling] = React.useState(false);
    const isBipInstalled = installedFromRedux || justInstalledBip;

    const variant = useVariant();
    const w = variant?.welcome || {};

    const title    = w.title    || 'BodaPro';
    const subtitle = w.subtitle || 'AI Native Productivity Platform';
    const aiLabel  = w.aiButtonLabel || 'Enable AI Assistant';
    const callouts = w.callouts || defaultCallouts;

    // `imageUrl` is a file:// URL resolved by main.js from `welcome.image` in variant.json,
    // and is only set when the file actually exists — a variant that declares a missing
    // image warns in the log and lands here on the bundled default, rather than on a
    // broken <img> that reads as a rendering fault instead of a packaging one.
    const imageSrc = w.imageUrl || SWEngImg;

    const handleEnableAI = async () => {
        setInstalling(true);
        try {
            if (window['bodapro-context']) {
                const result = await window['bodapro-context'].executeCommand('bodapro.extensions.install', { name: 'bip' });
                if (result.success) {
                    setJustInstalledBip(true);
                    window['bodapro-context'].showToast({
                        message: result.message || 'AI Assistant enabled successfully!',
                        intent: 'success'
                    });
                }
            }
        } catch (error) {
            console.error('Failed to install BIP extension:', error);
            window['bodapro-context']?.showToast({
                message: `Failed to enable AI Assistant: ${error.message}`,
                intent: 'danger'
            });
        } finally {
            setInstalling(false);
        }
    };

    return <div
        style={{
            padding: 5,
            height: '95vh',
            display: 'flex',
            flexDirection: 'column',
        }}>
        <div style={{ overflowY: 'auto', flex: 1, minHeight: 0 }}>
            <div style={{ margin: 50 }}>
                <h1 className="bp5-heading">{title}</h1>
                <Divider />
                <h3 className="bp5-text-muted" style={{ marginTop: 10 }}>{subtitle}</h3>
                <div style={{ marginTop: 15 }}>
                    <Button
                        text={isBipInstalled ? "Start AI Assistant" : aiLabel}
                        intent={Intent.SUCCESS}
                        loading={installing}
                        disabled={installing}
                        onClick={isBipInstalled ? () => window['bodapro-context']?.triggerCommand('bss.bip') : handleEnableAI}
                        icon={isBipInstalled ? "    " : "flash"}
                    />
                </div>
                <div style={{ marginTop: 50 }}>

                    <div style={{ display: 'flex', flexDirection: 'row' }} key="quick-actions">
                        <div style={{ flex: 1 }}>
                            <h3>Quick actions</h3>
                            <ul className="bp5-list">
                                <li>Browse documentation</li>
                                <li>Browse extensions</li>
                                <li>Get support</li>
                            </ul>
                        </div>
                        <div style={{ flex: 1 }}>
                            {/* Bounded rather than sized: a variant's image need not be
                                square, and forcing width=height=128 on a banner or a tall
                                graphic stretches it. `auto` on both dimensions lets the
                                aspect ratio survive. The default SVG is a 128px square, so
                                it renders exactly as before. */}
                            <img
                                src={imageSrc}
                                alt={w.imageAlt || `${title} illustration`}
                                style={{ maxWidth: '100%', maxHeight: 128, width: 'auto', height: 'auto' }}
                            />
                        </div>
                    </div>

                    <br />

                    {w.features && (
                        <div key="variant-features">
                            <h3>Features</h3>
                            <ul className="bp5-list">
                                {w.features.map((f, i) => <li key={i}>{f}</li>)}
                            </ul>
                            <br />
                        </div>
                    )}

                    {callouts.map((c, i) => {
                        // Normalize: explicit `actions` plus the legacy single `learnMoreUrl` button.
                        const actions = [
                            ...(Array.isArray(c.actions) ? c.actions : []),
                            ...(c.learnMoreUrl ? [{ label: c.learnMoreLabel || 'Read more', url: c.learnMoreUrl }] : []),
                        ];
                        return (
                            <div key={i}>
                                <Callout title={c.title}>
                                    {typeof c.body === 'string' ? <p>{c.body}</p> : c.body}
                                    {actions.length > 0 && (
                                        <>
                                            <hr />
                                            {actions.map((a, j) => (
                                                <Button
                                                    key={j}
                                                    text={a.label}
                                                    intent={a.intent || "primary"}
                                                    onClick={() => runAction(a)}
                                                    style={{ marginRight: 8 }}
                                                />
                                            ))}
                                        </>
                                    )}
                                </Callout>
                                <br />
                            </div>
                        );
                    })}

                </div>
            </div>
        </div>
    </div>;
}

export default Welcome;
